import {
    ADMIN_PAGES,
    DEFAULT_ROLE,
    DEFAULT_ROLE_DESCRIPTIONS,
    DEFAULT_ROLE_NAMES,
    DEFAULT_ROLE_PERMISSIONS,
    INITIAL_ROLES,
    MASTER_ROLE,
    PERMISSION_KEYS,
    ROLE_KEY_PATTERN,
    SYSTEM_ROLES,
    hasAdminAccess,
    isAdminPermission,
    isSystemRole,
    normalizeRole,
    resolvePermissionDetails,
    sanitizeOverrides,
    sanitizePermissionList
} from '../config/permissions';

const ROLES_COLLECTION = 'roles';
const USER_ACCESS_COLLECTION = 'user_access';

export function createAccessState() {
    return {
        // null - not checked yet, true - collection works, false - missing or not readable
        rolesAvailable: null,
        userAccessAvailable: null,
        roleRecords: {},
        ownAccess: null,
        userAccessRecords: {}
    };
}

export function resetAccessState(state) {
    Object.assign(state, createAccessState());
}

function logAccessError(message, error) {
    // eslint-disable-next-line no-console
    console.warn(message, {
        message: error?.message,
        status: error?.status
    });
}

function toRoleRecord(record) {
    return {
        id: record.id,
        key: normalizeRole(record.key),
        name: typeof record.name === 'string' ? record.name.trim() : '',
        description: typeof record.description === 'string' ? record.description : '',
        permissions: sanitizePermissionList(record.permissions)
    };
}

function toUserAccessRecord(record) {
    return {
        id: record.id,
        user: record.user,
        overrides: sanitizeOverrides(record.overrides),
        note: record.note || '',
        updated: record.updated
    };
}

function getDefaultRolePermissions(key) {
    if (key == MASTER_ROLE) return [...PERMISSION_KEYS];
    return [...(DEFAULT_ROLE_PERMISSIONS[key] || DEFAULT_ROLE_PERMISSIONS[DEFAULT_ROLE])];
}

export function createAccessModule(pb) {
    return {
        state: createAccessState,
        getters: {
            // Until roles are initialized in the DB, the initial roles (including vip) work with defaults.
            areRolesInitialized(state) {
                return Object.keys(state.roleRecords).length > 0;
            },
            roleExists(state, getters) {
                return role => {
                    const key = normalizeRole(role);
                    if (isSystemRole(key) || state.roleRecords[key]) return true;
                    return !getters.areRolesInitialized && INITIAL_ROLES.indexOf(key) != (-1);
                };
            },
            getRoleRecord(state) {
                return role => state.roleRecords[normalizeRole(role)] || null;
            },
            getRoleName(state) {
                return role => {
                    const key = normalizeRole(role);
                    const record = state.roleRecords[key];
                    if (record && record.name) return record.name;
                    if (DEFAULT_ROLE_NAMES[key]) return DEFAULT_ROLE_NAMES[key];
                    return key.charAt(0).toUpperCase() + key.slice(1);
                };
            },
            getRolePermissions(state, getters) {
                return role => {
                    const key = normalizeRole(role);
                    if (key == MASTER_ROLE) return [...PERMISSION_KEYS];

                    const record = state.roleRecords[key];
                    if (record && record.permissions) return record.permissions;

                    // Unknown or deleted roles get the base role permissions
                    if (!getters.roleExists(key)) return getters.getRolePermissions(DEFAULT_ROLE);

                    return getDefaultRolePermissions(key);
                };
            },
            // Order: user, custom roles by name, admin, master.
            getRolesList(state, getters) {
                const customKeys = getters.areRolesInitialized
                    ? Object.keys(state.roleRecords).filter(key => !isSystemRole(key))
                    : INITIAL_ROLES.filter(key => !isSystemRole(key));

                customKeys.sort((a, b) => getters.getRoleName(a).localeCompare(getters.getRoleName(b)));

                return [DEFAULT_ROLE, ...customKeys, ...SYSTEM_ROLES.filter(key => key != DEFAULT_ROLE)].map(key => {
                    const record = state.roleRecords[key] || null;

                    return {
                        key,
                        name: getters.getRoleName(key),
                        description: record ? record.description : (DEFAULT_ROLE_DESCRIPTIONS[key] || ''),
                        isSystem: isSystemRole(key),
                        record,
                        permissions: getters.getRolePermissions(key)
                    };
                });
            },
            getAssignableRoles(state, getters) {
                return getters.getRolesList.filter(role => role.key != MASTER_ROLE);
            },
            getUserOverrides(state) {
                return userId => {
                    const record = state.userAccessRecords[userId];
                    return record ? record.overrides : {};
                };
            },
            getUserAccessRecord(state) {
                return userId => state.userAccessRecords[userId] || null;
            },
            getUserPermissionDetails(state, getters) {
                return (user, overrides) => {
                    if (!user) return {};
                    const userOverrides = overrides || getters.getUserOverrides(user.id);
                    return resolvePermissionDetails(user.role, getters.getRolePermissions(user.role), userOverrides);
                };
            },
            getEffectivePermissions(state, getters, rootState) {
                const user = rootState.user;
                if (!user || !user.id) return {};

                const overrides = state.ownAccess ? state.ownAccess.overrides : {};
                return resolvePermissionDetails(user.role, getters.getRolePermissions(user.role), overrides);
            },
            can(state, getters, rootState) {
                return key => {
                    // Administration depends on the role only, it can not be granted or denied
                    if (isAdminPermission(key)) return !!(rootState.user && rootState.user.id && hasAdminAccess(rootState.user.role));

                    const permission = getters.getEffectivePermissions[key];
                    return !!(permission && permission.allowed);
                };
            },
            getAvailableAdminPages(state, getters) {
                return ADMIN_PAGES.filter(page => getters.can(page.permission));
            },
            getAccessStorageStatus(state) {
                return {
                    roles: state.rolesAvailable,
                    userAccess: state.userAccessAvailable
                };
            }
        },
        actions: {
            // Never rejects: on any problem the defaults are used, so login is never blocked.
            fetchAccess(context) {
                const user = context.rootState.user;
                const rolesRequest = context.dispatch('fetchRoles');

                const ownAccessRequest = (user && user.id)
                    ? pb.collection(USER_ACCESS_COLLECTION).getList(1, 1, {
                        filter: pb.filter('user = {:user}', { user: user.id })
                    })
                        .then(response => {
                            context.state.ownAccess = response.items.length ? toUserAccessRecord(response.items[0]) : null;
                            context.state.userAccessAvailable = true;
                        })
                        .catch(error => {
                            logAccessError('User access is not available, role permissions are used', error);
                            context.state.ownAccess = null;
                            context.state.userAccessAvailable = false;
                        })
                    : Promise.resolve();

                return Promise.all([rolesRequest, ownAccessRequest]).then(() => true);
            },
            // Never rejects: when roles are not readable the defaults are used.
            fetchRoles(context) {
                return pb.collection(ROLES_COLLECTION).getFullList()
                    .then(records => {
                        const roleRecords = {};
                        records.forEach(record => {
                            const role = toRoleRecord(record);
                            roleRecords[role.key] = role;
                        });
                        context.state.roleRecords = roleRecords;
                        context.state.rolesAvailable = true;
                    })
                    .catch(error => {
                        logAccessError('Roles are not available, default permissions are used', error);
                        context.state.roleRecords = {};
                        context.state.rolesAvailable = false;
                    });
            },
            // Never rejects, so pages depending on it never stay in loading state.
            fetchUserAccessList(context) {
                const status = context.rootState.resourceStatuses.userAccess;
                status.loading = true;

                return pb.collection(USER_ACCESS_COLLECTION).getFullList()
                    .then(records => {
                        const userAccessRecords = {};
                        records.forEach(record => {
                            const access = toUserAccessRecord(record);
                            userAccessRecords[access.user] = access;
                        });
                        context.state.userAccessRecords = userAccessRecords;
                        context.state.userAccessAvailable = true;
                    })
                    .catch(error => {
                        logAccessError('User access list is not available', error);
                        context.state.userAccessRecords = {};
                        context.state.userAccessAvailable = false;
                    })
                    .finally(() => {
                        status.loaded = true;
                        status.loading = false;
                    });
            },
            // Creates DB records for the initial roles with the current default permissions.
            initializeRoles(context) {
                return context.dispatch('fetchRoles')
                    .then(() => {
                        if (!context.state.rolesAvailable) throw new Error('Roles collection is not available');

                        // PocketBase rules let only the owner create the owner role record
                        const isOwner = normalizeRole(context.rootState.user.role) == MASTER_ROLE;
                        const missing = INITIAL_ROLES.filter(key => {
                            return !context.state.roleRecords[key] && (key != MASTER_ROLE || isOwner);
                        });

                        return missing.reduce((chain, key) => chain.then(() => {
                            return pb.collection(ROLES_COLLECTION).create({
                                key,
                                name: DEFAULT_ROLE_NAMES[key],
                                description: DEFAULT_ROLE_DESCRIPTIONS[key] || '',
                                // Master permissions are not stored, owner always has full access
                                permissions: key == MASTER_ROLE ? [] : getDefaultRolePermissions(key)
                            });
                        }), Promise.resolve())
                            .then(() => missing.length);
                    })
                    .then(created => context.dispatch('fetchRoles').then(() => created));
            },
            // The first write creates every initial role, otherwise roles working with defaults
            // (for example vip) would lose their permissions once the collection is not empty.
            ensureRolesInitialized(context) {
                if (context.getters.areRolesInitialized) return Promise.resolve(0);
                return context.dispatch('initializeRoles');
            },
            saveRole(context, params) {
                const key = normalizeRole(params.key);
                const payload = {};

                if (params.name !== undefined) payload.name = String(params.name).trim();
                if (params.description !== undefined) payload.description = String(params.description).trim();
                if (params.permissions !== undefined) payload.permissions = sanitizePermissionList(params.permissions) || [];

                return context.dispatch('ensureRolesInitialized')
                    .then(() => {
                        const existing = context.state.roleRecords[key];
                        if (existing) return pb.collection(ROLES_COLLECTION).update(existing.id, payload);
                        if (!isSystemRole(key)) throw new Error('Role not found');

                        // System role record was removed from the DB manually
                        return pb.collection(ROLES_COLLECTION).create({
                            key,
                            name: payload.name !== undefined ? payload.name : DEFAULT_ROLE_NAMES[key],
                            description: payload.description !== undefined ? payload.description : (DEFAULT_ROLE_DESCRIPTIONS[key] || ''),
                            permissions: payload.permissions !== undefined
                                ? payload.permissions
                                : (key == MASTER_ROLE ? [] : getDefaultRolePermissions(key))
                        });
                    })
                    .then(record => {
                        const role = toRoleRecord(record);
                        context.state.roleRecords = { ...context.state.roleRecords, [role.key]: role };
                        context.state.rolesAvailable = true;
                        return role;
                    });
            },
            createRole(context, params) {
                const key = String(params.key || '').trim().toLowerCase();
                const name = String(params.name || '').trim();

                if (!ROLE_KEY_PATTERN.test(key)) {
                    return Promise.reject(new Error('Role ID must be 2-32 chars: latin letters, digits, "-" or "_", starting with a letter'));
                }
                if (!name.length) return Promise.reject(new Error('Role name is required'));

                // Fresh roles from the server, another admin could create the same key meanwhile.
                // The unique index on roles.key protects from a race between these two requests.
                return context.dispatch('ensureRolesInitialized')
                    .then(() => context.dispatch('fetchRoles'))
                    .then(() => {
                        if (context.getters.roleExists(key)) {
                            throw new Error(`Role with ID "${key}" already exists`);
                        }

                        return pb.collection(ROLES_COLLECTION).create({
                            key,
                            name,
                            description: String(params.description || '').trim(),
                            permissions: sanitizePermissionList(params.permissions) || getDefaultRolePermissions(DEFAULT_ROLE)
                        });
                    })
                    .then(record => {
                        const role = toRoleRecord(record);
                        context.state.roleRecords = { ...context.state.roleRecords, [role.key]: role };
                        context.state.rolesAvailable = true;
                        return role;
                    });
            },
            // System roles can not be deleted. Users of the role are moved to the base role first,
            // the role itself is deleted only when every user was moved.
            deleteRole(context, roleKey) {
                const key = normalizeRole(roleKey);
                const existing = context.state.roleRecords[key];

                if (isSystemRole(key)) return Promise.reject(new Error('System roles can not be deleted'));
                if (!existing) return Promise.reject(new Error('Role not found'));

                return pb.collection('users').getFullList({
                    filter: pb.filter('role = {:role}', { role: key }),
                    fields: 'id,role'
                })
                    .then(users => {
                        // Sequential updates keep the load on the server low and stop on the first error
                        return users.reduce((chain, user) => chain.then(() => {
                            return pb.collection('users').update(user.id, { role: DEFAULT_ROLE })
                                .then(() => {
                                    const localUser = context.rootState.users.find(item => item.id == user.id);
                                    if (localUser) localUser.role = DEFAULT_ROLE;
                                });
                        }), Promise.resolve())
                            .then(() => users.length);
                    })
                    .then(movedUsers => pb.collection(ROLES_COLLECTION).delete(existing.id).then(() => movedUsers))
                    .then(movedUsers => {
                        const records = { ...context.state.roleRecords };
                        delete records[key];
                        context.state.roleRecords = records;
                        return movedUsers;
                    });
            },
            saveUserOverrides(context, params) {
                const overrides = sanitizeOverrides(params.overrides);
                const existing = context.state.userAccessRecords[params.userId];
                const hasOverrides = Object.keys(overrides).length > 0;
                const note = params.note !== undefined ? String(params.note).trim() : (existing ? existing.note : '');

                if (params.userId == context.rootState.user.id) {
                    return Promise.reject(new Error('You can not change your own access'));
                }

                let request;

                if (existing && !hasOverrides && !note.length) {
                    request = pb.collection(USER_ACCESS_COLLECTION).delete(existing.id).then(() => null);
                } else if (existing) {
                    request = pb.collection(USER_ACCESS_COLLECTION).update(existing.id, { overrides, note });
                } else if (hasOverrides || note.length) {
                    request = pb.collection(USER_ACCESS_COLLECTION).create({ user: params.userId, overrides, note });
                } else {
                    return Promise.resolve(null);
                }

                return request.then(record => {
                    const records = { ...context.state.userAccessRecords };
                    if (record) {
                        records[params.userId] = toUserAccessRecord(record);
                    } else {
                        delete records[params.userId];
                    }
                    context.state.userAccessRecords = records;
                    context.state.userAccessAvailable = true;
                    return record;
                });
            },
            // Updates only the role field, so other user data is never overwritten.
            updateUserRole(context, params) {
                const role = normalizeRole(params.role);
                const target = context.rootState.users.find(item => item.id == params.userId);

                if (role == MASTER_ROLE) return Promise.reject(new Error('Owner role can not be assigned'));
                if (!context.getters.roleExists(role)) return Promise.reject(new Error('Role not found'));
                if (target && normalizeRole(target.role) == MASTER_ROLE) {
                    return Promise.reject(new Error('Owner role can not be changed'));
                }
                if (params.userId == context.rootState.user.id) {
                    return Promise.reject(new Error('You can not change your own role'));
                }

                return pb.collection('users').update(params.userId, { role })
                    .then(record => {
                        if (target) target.role = record.role;
                        return record;
                    });
            }
        }
    };
}
